import React, { useEffect, useState } from "react";

import * as api from "../api";
import Pill from "../components/Pill";
import type { PageProps } from "./types";

const th: React.CSSProperties = { fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-muted)", fontWeight: 400, borderBottom: "1px solid var(--border-hairline)" };

function fmtWindow(startIso: string, endIso: string): string {
  const s = new Date(startIso);
  const e = new Date(endIso);
  const day = s.toLocaleDateString([], { month: "short", day: "numeric" });
  const t = (d: Date) => d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return `${day} · ${t(s)}–${t(e)}`;
}

function StatCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <div style={{ background: "#fff", border: "1px solid var(--border-hairline)", borderRadius: 14, padding: "18px 20px", boxShadow: "var(--elevation-sm)" }}>
      <div style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, letterSpacing: "0.12em", textTransform: "uppercase", color: "var(--text-muted)" }}>{label}</div>
      <div style={{ fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 28, letterSpacing: "-0.02em", color, marginTop: 10 }}>{value}</div>
    </div>
  );
}

export default function Reconciliation(_props: PageProps) {
  const [runs, setRuns] = useState<api.ReconciliationRun[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.listReconciliationRuns(30).then(
      (r) => setRuns(r.runs),
      (e) => { setError(e?.message || "Reconciliation unavailable"); setRuns([]); },
    );
  }, []);

  const list = runs || [];
  const totChecked = list.reduce((a, r) => a + r.calls_checked, 0);
  const totCorr = list.reduce((a, r) => a + r.corrections, 0);
  const totDisc = list.reduce((a, r) => a + r.discrepancies, 0);

  return (
    <div style={{ padding: "28px 32px 48px", maxWidth: 1120 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16, marginBottom: 24 }}>
        <StatCard label="Runs (recent)" value={String(list.length)} color="var(--ink-900)" />
        <StatCard label="Calls checked" value={totChecked.toLocaleString()} color="var(--ink-900)" />
        <StatCard label="Corrections" value={String(totCorr)} color="var(--ink-900)" />
        <StatCard label="Discrepancies" value={String(totDisc)} color={totDisc ? "var(--danger-500)" : "var(--ink-900)"} />
      </div>

      <div style={{ background: "#fff", border: "1px solid var(--border-hairline)", borderRadius: 16, boxShadow: "var(--elevation-sm)", overflow: "hidden" }}>
        <div style={{ padding: "18px 22px", borderBottom: "1px solid var(--border-hairline)" }}>
          <h2 style={{ margin: 0, fontFamily: "var(--font-display)", fontWeight: 600, fontSize: 16, color: "var(--ink-900)" }}>Telephony reconciliation runs</h2>
          <p style={{ margin: "4px 0 0", fontSize: 12.5, color: "var(--text-muted)" }}>Hourly ledger reconciliation — corrections applied, discrepancies flagged (US4).</p>
        </div>
        {error && <p style={{ padding: "12px 22px", color: "var(--danger-500)", fontSize: 13 }}>{error}</p>}
        {runs && list.length > 0 ? (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={{ ...th, textAlign: "left", padding: "11px 22px" }}>Window</th>
              <th style={{ ...th, textAlign: "right", padding: "11px 14px" }}>Checked</th>
              <th style={{ ...th, textAlign: "right", padding: "11px 14px" }}>Corrections</th>
              <th style={{ ...th, textAlign: "right", padding: "11px 14px" }}>Discrepancies</th>
              <th style={{ ...th, textAlign: "right", padding: "11px 14px" }}>Unattributable</th>
              <th style={{ ...th, textAlign: "left", padding: "11px 22px" }}>Status</th>
            </tr></thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.id}>
                  <td style={{ padding: "13px 22px", borderBottom: "1px solid var(--border-hairline)", fontFamily: "var(--font-mono)", fontSize: 12.5, color: "var(--ink-900)" }}>{fmtWindow(r.window_start, r.window_end)}</td>
                  <td style={{ padding: "13px 14px", borderBottom: "1px solid var(--border-hairline)", textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--ink-700)" }}>{r.calls_checked.toLocaleString()}</td>
                  <td style={{ padding: "13px 14px", borderBottom: "1px solid var(--border-hairline)", textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--ink-700)" }}>{r.corrections}</td>
                  <td style={{ padding: "13px 14px", borderBottom: "1px solid var(--border-hairline)", textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 13, color: r.discrepancies > 0 ? "var(--danger-500)" : "var(--ink-700)", fontWeight: r.discrepancies > 0 ? 700 : 400 }}>{r.discrepancies}</td>
                  <td style={{ padding: "13px 14px", borderBottom: "1px solid var(--border-hairline)", textAlign: "right", fontFamily: "var(--font-mono)", fontSize: 13, color: "var(--ink-700)" }}>{r.unattributable}</td>
                  <td style={{ padding: "13px 22px", borderBottom: "1px solid var(--border-hairline)" }}><Pill tone={r.status === "ok" ? "ok" : "warn"}>{r.status === "ok" ? "ok" : "attention"}</Pill></td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : runs && list.length === 0 && !error ? (
          <div style={{ padding: 48, textAlign: "center", color: "var(--text-muted)", fontSize: 14 }}>No recent reconciliation runs — this itself is a signal that reconciliation may not be running.</div>
        ) : !runs && !error ? (
          <div style={{ padding: 48, textAlign: "center", color: "var(--text-muted)", fontSize: 14 }}>Loading…</div>
        ) : null}
      </div>
    </div>
  );
}
